/**
 * Fallback label derivation for tags the backend doesn't ship a label for.
 *
 * Since #2390 the backend `SupportedTagsDto` carries optional `fieldTagLabels`,
 * `groupLabels`, and `groupColumnLabels` — the mapper (via IDocumentSourceTagCatalog)
 * owns those and both the in-Suite Tag Reference panel and the Word add-in read
 * from them as their primary label source. This function is only invoked when the
 * DTO leaves a tag unlabelled — a stub source type whose mapper hasn't yet
 * populated labels, or an older server predating #2390.
 *
 *   - `CF_UPPER_SNAKE_CASE` → "Upper Snake Case" (drops the CF_ prefix,
 *     splits on underscore, title-cases each word)
 *   - `PascalCase`         → "Pascal Case" (inserts a space before each
 *     capital that follows a lowercase / digit)
 *   - `ALLCAPS`            → "ALLCAPS" (no lowercase → no split)
 *
 * Not a perfect semantic label — e.g. `CF_NAME` labelizes to "Name" even
 * though it carries the configuration number. That's exactly the class of
 * miss #2390 solves by moving labels to the mapper.
 */
export function toLabel(tag: string): string {
  if (tag.startsWith('CF_')) {
    return tag.substring(3)
      .split('_')
      .filter((word) => word.length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // PascalCase columns + region names → space-separated. All-caps acronyms
  // ("SF") stay intact because there's no lowercase-followed-by-uppercase
  // boundary to split on.
  return tag.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}
